import React, { useEffect, useState, useContext } from "react";
import {
  Button,
  TextField,
  Box,
  Typography,
  Container,
  Snackbar,
  Stack,
  InputAdornment,
} from "@mui/material";
import { PhotoCamera } from "@mui/icons-material";
import { useFormik } from "formik";
import * as yup from "yup";
import { getUser, listUsers } from "../../graphql/queries";
import { updateUser } from "../../graphql/mutations";
import { API, graphqlOperation, Auth } from "aws-amplify";
import { getOrCreateUser } from "../../services/userManagementService";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../App";
import { Amplify } from "aws-amplify";

const validationSchema = yup.object({
  email: yup
    .string("Enter email")
    .email("Enter a valid email")
    .required("Email is required"),
});

const EmailInvite = ({ username, onComplete, inviteCognitoUser = true }) => {
  const { userRecord } = useContext(AppContext); // Get the user from the AppContext
  const user = userRecord;
  console.log("user in email form", user);
  const [userDetails, setUserDetails] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [userFeedback, setUserFeedback] = useState(null);

  const navigate = useNavigate();

  const popFeedback = (message) => {
    setUserFeedback(message);
    setShowFeedback(true);
  };

  const formik = useFormik({
    initialValues: {
      email: userDetails?.email || "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (!userRecord || !userRecord.id) {
        popFeedback("User information not available. Please try again.");
        return;
      }
      checkUser(values.email);
    },
  });

  console.log("got username in params", username);

  async function createCognitoUser(email, password) {
    try {
      const user = await Amplify.Auth.signUp({
        username: email,
        password,
        attributes: {
          email, // optional attribute, can be used to set custom attributes
        },
      });

      console.log("User signed up:", user);

      // Trigger the verification email

      await Auth.resendSignUp(user);
      await Auth.sendSignUpCode(user);
      console.log(`Verification code sent to ${user.attributes.email}`);
    } catch (error) {
      console.error("Error signing up user:", error);

      // Handle duplicate email/user errors
      if (
        error.code === "UsernameExistsException" ||
        error.code === "AliasExistsException" ||
        error.message?.includes("already exists") ||
        error.message?.includes("duplicate")
      ) {
        popFeedback(
          "An account with this email address already exists in Cognito."
        );
      } else {
        popFeedback(
          `Error creating Cognito user: ${error.message || "Unknown error"}`
        );
      }
    }
  }

  async function createCognitoUserBroke(username, password, email) {
    try {
      await Auth.signUp({
        username,
        password,
        attributes: {
          email,
        },
      });

      console.log(`Verification code sent to ${email}`);
    } catch (error) {
      console.error("Error signing up user:", error);
    }
  }

  const checkUser = async (email) => {
    if (!userRecord || !userRecord.id) {
      popFeedback("User information not available. Please try again.");
      return;
    }

    if (inviteCognitoUser) {
      createCognitoUser(email, "Temp1234");
    }

    try {
      // Use getOrCreateUser which handles race conditions and case-insensitive email matching
      const userData = {
        email: email.toLowerCase().trim(),
        username: email.toLowerCase().trim(),
        searchesRemaining: 100,
        createdByUserId: userRecord.id,
        // Note: priceListInvites should be updated separately after getting the user
      };

      console.log("Creating/inviting user with getOrCreateUser:", userData);
      const createdUser = await getOrCreateUser(userData);

      // Now update the priceListInvites on the created/existing user
      const userToUpdate = {
        id: createdUser.id,
        priceListInvites: [userRecord.id],
      };

      if (
        !createdUser.priceListInvites ||
        !createdUser.priceListInvites.includes(userRecord.id)
      ) {
        const updatedUserResponse = await API.graphql(
          graphqlOperation(updateUser, { input: userToUpdate })
        );
        console.log("Updated user priceListInvites:", updatedUserResponse);
      } else {
        console.log("User already has this pricelist invite");
      }

      popFeedback("User invite sent successfully.");
      navigate(`/users`);
    } catch (error) {
      console.error("Error creating/inviting user:", error);
      // Handle specific error cases
      if (error.message && error.message.includes("already exists")) {
        popFeedback(
          "An account with this email address already exists in the database."
        );
      } else if (error.message) {
        popFeedback(`Error creating user: ${error.message}`);
      } else {
        popFeedback("An unexpected error occurred.");
      }
    }
  };

  const handleClose = () => {
    setShowFeedback(false);
  };

  useEffect(() => {
    if (userDetails) {
      formik.setValues({
        email: userDetails.email || "",
      });
    }
  }, [userDetails]);

  // Show loading state if userRecord is not available
  if (!userRecord || !userRecord.id) {
    return (
      <Container>
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Loading user information...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        noValidate
        sx={{ mt: 4 }}
      >
        <Typography variant="h6" gutterBottom>
          Invite a User
        </Typography>
        {/* <Button variant="contained" startIcon={<PhotoCamera />} sx={{ mb: 2 }}>
          Scan QR Code
        </Button> */}

        <TextField
          fullWidth
          id="email"
          name="email"
          label="Primary Email Address"
          value={formik.values.email}
          onChange={formik.handleChange}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          margin="normal"
          required
        />

        <hr />
        <Stack spacing={2} direction="row">
          <Button
            variant="contained"
            type="submit"
            sx={{ mt: 2 }}
            disabled={!formik.isValid}
          >
            Submit
          </Button>
        </Stack>

        <Snackbar
          open={showFeedback}
          autoHideDuration={1000}
          onClose={handleClose}
          message={userFeedback}
        />
      </Box>
    </Container>
  );
};

export default EmailInvite;
